import { Link } from 'react-router-dom';

/**
 * Privacy Policy для SWIPA — публичная версия. Покрывает требования 152-ФЗ
 * (РФ), GDPR (EU), Google Play Data Safety form, Apple App Store privacy
 * labels (когда iOS будет в App Store).
 *
 * Параллельная статичная копия — `web/public/privacy.html` — для Play
 * crawler / Apple review (которые могут не выполнить SPA JS).
 */
export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-surface-950 text-surface-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="text-brand-400 hover:text-brand-300 text-sm mb-4 inline-block">
          ← На главную
        </Link>

        <h1 className="text-3xl font-bold mb-2">Политика конфиденциальности</h1>
        <p className="text-surface-400 text-sm mb-8">Последнее обновление: 6 мая 2026</p>

        <section className="space-y-4 text-sm leading-relaxed">
          <h2 className="text-xl font-semibold mt-6">1. Общие положения</h2>
          <p>
            Настоящая Политика регулирует обработку персональных данных пользователей сервиса
            SWIPA (домен <strong>swipa.ru</strong>, ранее chat56.ru), а также мобильных
            приложений для Android и iOS под идентификатором <code>app.swipa</code>.
          </p>
          <p>
            Используя сервис, вы соглашаетесь с обработкой данных в соответствии с настоящей
            Политикой. Если вы не согласны — пожалуйста, прекратите использование и удалите
            аккаунт через настройки приложения.
          </p>

          <h2 className="text-xl font-semibold mt-6">2. Какие данные мы обрабатываем</h2>

          <h3 className="font-semibold mt-4">2.1. Регистрационные данные</h3>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>имя пользователя (username)</li>
            <li>отображаемое имя (displayName)</li>
            <li>email — для подтверждения и восстановления аккаунта</li>
            <li>хэш пароля (bcrypt с уникальной солью)</li>
            <li>опционально: TOTP-секрет (зашифрован AES-256-GCM в БД), WebAuthn ключи</li>
          </ul>

          <h3 className="font-semibold mt-4">2.2. Данные аутентификации и сессий</h3>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>IP-адрес и User-Agent при логине (для журнала безопасности)</li>
            <li>refresh-токены и метаданные сессий (для multi-device cap и принудительного logout)</li>
            <li>журнал отказов в логине (failed_logins) — для rate limit</li>
          </ul>

          <h3 className="font-semibold mt-4">2.3. Контент и взаимодействия</h3>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>текст сообщений (зашифрован AES-256-GCM на диске; полнотекстовый индекс в открытом виде для поиска <em>только в ваших</em> диалогах)</li>
            <li>в «секретных чатах» — содержимое end-to-end зашифровано (X3DH-lite + Double Ratchet); сервер видит только зашифрованный blob</li>
            <li>загруженные медиафайлы (изображения, видео, голосовые) — зашифрованы AES-256-GCM на сервере; TTL — 7 дней с момента последнего обращения</li>
            <li>реакции, закладки, история просмотров видео и сторис</li>
            <li>граф подписок и друзей</li>
            <li>karma score (численная репутация на основе поведения внутри сервиса)</li>
          </ul>

          <h3 className="font-semibold mt-4">2.4. Технические данные</h3>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>push-токены: FCM (Android), VAPID Web Push (web/PWA), APNs (iOS — when shipped)</li>
            <li>версия приложения, язык интерфейса, тип устройства</li>
            <li>время последнего онлайна (можно скрыть в настройках Privacy)</li>
          </ul>

          <h3 className="font-semibold mt-4">2.5. Данные, которые мы НЕ собираем</h3>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>геолокация (никогда — приложение не запрашивает разрешение)</li>
            <li>контакты с устройства</li>
            <li>история звонков на устройстве (только звонки внутри SWIPA)</li>
            <li>список установленных приложений</li>
            <li>рекламные идентификаторы (Google Advertising ID / IDFA)</li>
            <li>биометрия (TouchID/FaceID — обрабатывается на устройстве, до сервера не доходит)</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6">3. Цели обработки</h2>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>аутентификация и предоставление функциональности мессенджера</li>
            <li>доставка push-уведомлений (можно настроить per-type в Settings)</li>
            <li>выявление и предотвращение злоупотреблений (rate limit, refresh-token reuse detection)</li>
            <li>модерация контента (автоматическая NSFW-классификация, banned words)</li>
            <li>персонализированные рекомендации в ленте «Для вас» (на основе истории просмотров и графа подписок — алгоритм работает на наших серверах, не передаётся внешним рекламным платформам)</li>
            <li>выполнение требований законодательства РФ и юрисдикций пользователей</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6">4. Шифрование</h2>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>все сетевые соединения — TLS 1.3 (HTTPS/WSS)</li>
            <li>cert pinning на корневые сертификаты ISRG Root X1/X2 в мобильных клиентах</li>
            <li>пароли — bcrypt с уникальной солью</li>
            <li>содержимое сообщений на диске — AES-256-GCM</li>
            <li>«Секретные чаты» — end-to-end (X3DH-lite + Double Ratchet); сервер blind-relay</li>
            <li>медиафайлы — AES-256-GCM с уникальным IV на файл</li>
            <li>токены аутентификации — Android Keystore / iOS Keychain</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6">5. Третьи стороны</h2>

          <h3 className="font-semibold mt-4">5.1. Сервисы push-уведомлений</h3>
          <p>
            Для доставки push-уведомлений используются:
          </p>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li><strong>Firebase Cloud Messaging (Google LLC)</strong> — Android. Передаётся: push-токен устройства, минимальный payload (заголовок, обрезанный текст). Содержимое сообщения <em>не</em> отправляется в FCM.</li>
            <li><strong>Web Push (VAPID)</strong> — браузеры/PWA. Endpoint браузера + подписанный payload. Без участия сторонних сервисов.</li>
            <li><strong>Apple Push Notification service</strong> — iOS (когда iOS-приложение запустится). Push-токен + VoIP push для CallKit.</li>
          </ul>

          <h3 className="font-semibold mt-4">5.2. Транскрипция голосовых сообщений</h3>
          <p>
            Если вы или собеседник запросил субтитры/транскрипт голосового сообщения, аудио-файл
            может быть отправлен внешнему провайдеру распознавания речи. Сейчас используются:
            <strong> Groq</strong> (Whisper-large-v3), <strong>Yandex SpeechKit</strong>,
            а также локальный Whisper-провайдер на нашем сервере. Транскрипт сохраняется
            рядом с аудио-файлом и подчиняется тому же TTL (7 дней).
          </p>
          <p>
            Транскрипция <strong>не выполняется</strong> для сообщений в секретных чатах —
            там аудио зашифровано E2E и сервер не имеет к нему доступа.
          </p>

          <h3 className="font-semibold mt-4">5.3. Платежи</h3>
          <p>
            Платежи в магазине стикеров проводятся внутренней «валютой контента» SWIPA. Мы
            <em> не</em> используем сторонние платёжные процессоры на момент публикации настоящей
            Политики. При появлении такой интеграции Политика будет обновлена.
          </p>

          <h2 className="text-xl font-semibold mt-6">6. Сроки хранения</h2>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>сообщения и реакции — пока вы не удалите их или аккаунт</li>
            <li>медиафайлы — 7 дней с момента последнего обращения, далее удаляются с диска</li>
            <li>журнал безопасности (audit_log) — 90 дней</li>
            <li>журнал отказов в логине — 24 часа</li>
            <li>бэкапы PostgreSQL — 7 дней rolling</li>
            <li>после удаления аккаунта — мягкое удаление в течение 30 дней (для возможности восстановления), затем hard-delete</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6">7. Ваши права (152-ФЗ ст. 14, GDPR Art. 15-22)</h2>

          <h3 className="font-semibold mt-4">7.1. Право доступа и переносимости (Right to Access / Portability — GDPR Art. 15, 20)</h3>
          <p>
            Вы можете в любой момент скачать полную JSON-выгрузку всех ваших данных через
            <code> Настройки → Аккаунт → Экспорт данных</code>. Выгрузка содержит регистрационные
            данные, сообщения, реакции, ленту подписок, ссылки на ваши медиафайлы.
          </p>

          <h3 className="font-semibold mt-4">7.2. Право на удаление (Right to Erasure — GDPR Art. 17, 152-ФЗ ст. 14 ч. 7)</h3>
          <p>
            Вы можете удалить аккаунт через <code>Настройки → Аккаунт → Удалить аккаунт</code>.
            Аккаунт переходит в состояние «scheduled for deletion» на 30 дней — в течение этого
            периода вы можете восстановить его по email-ссылке. По истечении 30 дней все данные
            необратимо удаляются (hard-delete) автоматическим cron-заданием.
          </p>

          <h3 className="font-semibold mt-4">7.3. Право на исправление</h3>
          <p>
            Вы можете в любой момент изменить displayName, email, аватар, описание профиля
            через <code>Настройки → Профиль</code>. Изменение username — по запросу в поддержку.
          </p>

          <h3 className="font-semibold mt-4">7.4. Право на ограничение обработки</h3>
          <p>
            В <code>Настройки → Конфиденциальность</code> вы можете:
          </p>
          <ul className="list-disc list-inside space-y-1 text-surface-300">
            <li>скрыть «был онлайн»</li>
            <li>скрыть онлайн-статус</li>
            <li>отключить «прочитано» (read receipts)</li>
            <li>ограничить, кто может вам звонить (Все / Друзья / Никто)</li>
            <li>ограничить, кто может приглашать вас в каналы</li>
            <li>включить «тихие часы» для push-уведомлений</li>
            <li>отключить отдельные типы push-уведомлений</li>
          </ul>

          <h3 className="font-semibold mt-4">7.5. Право на отзыв согласия</h3>
          <p>
            Согласие на обработку даётся при регистрации (явный чекбокс) и может быть отозвано
            в любой момент путём удаления аккаунта (см. 7.2).
          </p>

          <h2 className="text-xl font-semibold mt-6">8. Несовершеннолетние</h2>
          <p>
            Сервис не предназначен для лиц младше 13 лет (COPPA). Контент категории 18+ скрыт
            по умолчанию и доступен только пользователям, явно подтвердившим возраст и достигшим
            определённого уровня репутации в сервисе. В версии для Google Play 18+ контент
            недоступен в принципе — отдельный билд без UI-переключателя.
          </p>

          <h2 className="text-xl font-semibold mt-6">9. Изменения Политики</h2>
          <p>
            Мы можем обновлять Политику. О существенных изменениях мы уведомим внутри приложения
            (баннер при следующем входе) и/или по email. Дата последней редакции указана выше.
          </p>

          <h2 className="text-xl font-semibold mt-6">10. Контакты</h2>
          <p>
            Вопросы по конфиденциальности, запросы на доступ/удаление, претензии:
            <br />
            <a href="mailto:privacy@swipa.ru" className="text-brand-400">privacy@swipa.ru</a>
          </p>
          <p>
            Общая поддержка: <a href="mailto:support@swipa.ru" className="text-brand-400">support@swipa.ru</a>
          </p>
          <p>
            Согласно 152-ФЗ ст. 22, пользователь также может обратиться с жалобой в
            Роскомнадзор: <a href="https://rkn.gov.ru" className="text-brand-400" target="_blank" rel="noopener noreferrer">rkn.gov.ru</a>.
            Пользователи EU/EEA — в надзорный орган своей страны (CNIL, BfDI и т.д.).
          </p>
        </section>
      </div>
    </div>
  );
}
